import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Play, Square, AlertTriangle } from 'lucide-react';
import { isPortInUseError } from '../../services/mailCatcherService';
import { isMacPlatform } from '../../utils/keyboard';

/**
 * Top bar, mail-client shape (owner mockup 08.09.2026):
 *   [● 127.0.0.1:1025] [Stop]   [🔍 Search subject, sender, body…  ⌘F]   12 caught · 2 unread  [Clear all]
 * The status pill only SHOWS; the Start/Stop button beside it acts. Pure
 * presentation — the view owns the store calls and the ⌘F focus.
 */
export default function MailCatcherToolbar({
  status,
  starting,
  preferredPort,
  query,
  total,
  unread,
  searchRef,
  onStart,
  onStop,
  onQueryChange,
  onClearAll,
}) {
  const { t } = useTranslation();
  const running = !!status.running;
  const port = running && status.port ? status.port : preferredPort;
  const error = status.lastError;

  return (
    <div className="mailc-toolbar">
      <span
        className={`mailc-status${running ? ' mailc-status--on' : ''}`}
        data-testid="mail-status"
        title={running ? t('mailCatcher.statusRunning', { port }) : t('mailCatcher.statusStopped')}
      >
        <span className={`mailc-dot${running ? ' mailc-dot--on' : ''}`} />
        <span className="mailc-status-text">{running ? `127.0.0.1:${port}` : t('mailCatcher.statusStopped')}</span>
      </span>
      <button
        type="button"
        className="mailc-btn mailc-btn--toggle"
        onClick={running ? onStop : onStart}
        disabled={starting}
        aria-pressed={running}
        data-testid="mail-toggle"
      >
        {running ? <Square size={11} /> : <Play size={11} />}
        {starting ? t('mailCatcher.starting') : running ? t('mailCatcher.stop') : t('mailCatcher.start')}
      </button>

      {error && (
        <span className="mailc-error" role="alert" data-testid="mail-error">
          <AlertTriangle size={13} />
          {isPortInUseError(error)
            ? t('mailCatcher.portInUse', { port: preferredPort })
            : t('mailCatcher.startFailed', { error })}
        </span>
      )}

      <label className="mailc-search">
        <Search size={14} />
        <input
          ref={searchRef}
          type="text"
          value={query}
          placeholder={t('mailCatcher.searchPlaceholder')}
          aria-label={t('mailCatcher.search')}
          onChange={(e) => onQueryChange(e.target.value)}
        />
        {query ? (
          <button type="button" className="mailc-search-clear" aria-label="clear search" onClick={() => onQueryChange('')}>
            <X size={12} />
          </button>
        ) : (
          <kbd className="mailc-kbd" aria-hidden="true">{isMacPlatform() ? '⌘F' : 'Ctrl F'}</kbd>
        )}
      </label>

      <span className="mailc-counts" data-testid="mail-counts">
        <span>{t('mailCatcher.caughtCount', { count: total })}</span>
        {unread > 0 && (
          <>
            <span className="mailc-counts-sep">·</span>
            <span className="mailc-counts-unread">{t('mailCatcher.unreadCount', { count: unread })}</span>
          </>
        )}
      </span>

      <button type="button" className="mailc-btn" onClick={onClearAll} disabled={total === 0} title={t('mailCatcher.clearAll')}>
        {t('mailCatcher.clearAll')}
      </button>
    </div>
  );
}
